
import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../types';

interface ActivityLogTabProps {
  users: User[];
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

const actionLabels: Record<string, { label: string; color: string }> = {
  'login': { label: 'Connexion', color: 'bg-emerald-100 text-emerald-700' },
  'login_failed': { label: 'Connexion échouée', color: 'bg-rose-100 text-rose-700' },
  'logout': { label: 'Déconnexion', color: 'bg-amber-100 text-amber-700' },
  'logout_inactivity': { label: 'Inactivité', color: 'bg-orange-100 text-orange-700' },
  'create_lead': { label: 'Création prospect', color: 'bg-indigo-100 text-indigo-700' },
  'update_lead': { label: 'Modification prospect', color: 'bg-sky-100 text-sky-700' },
  'delete_lead': { label: 'Suppression prospect', color: 'bg-rose-100 text-rose-700' },
  'bulk_update_leads': { label: 'Modification en masse', color: 'bg-purple-100 text-purple-700' },
  'bulk_delete_leads': { label: 'Suppression en masse', color: 'bg-rose-100 text-rose-700' },
  'create_user': { label: 'Création utilisateur', color: 'bg-teal-100 text-teal-700' },
  'delete_user': { label: 'Suppression utilisateur', color: 'bg-rose-100 text-rose-700' },
  'update_profile': { label: 'Modification profil', color: 'bg-slate-100 text-slate-700' },
  'push_to_erp': { label: 'Export ERP', color: 'bg-cyan-100 text-cyan-700' },
  'view_pii_leads':               { label: 'Consultation PII prospects',   color: 'bg-violet-100 text-violet-700' },
  'view_pii_users':               { label: 'Consultation PII utilisateurs', color: 'bg-purple-100 text-purple-700' },
  'gdpr_export':                  { label: 'Export RGPD',                   color: 'bg-sky-100 text-sky-700' },
  'gdpr_erase':                   { label: 'Effacement RGPD',               color: 'bg-rose-100 text-rose-700' },
  'support_cross_company_access': { label: 'Accès inter-entreprise',        color: 'bg-amber-100 text-amber-700' },
};

const ActivityLogTab: React.FC<ActivityLogTabProps> = ({ users }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
      const params = new URLSearchParams({ action: 'get_audit_logs', page: String(page), limit: '30' });
      if (filterAction) params.set('filter_action', filterAction);
      if (filterUser) params.set('filter_user', filterUser);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await fetch(`${API_BASE}?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setLogs(data.logs || []);
      setLogTotal(data.total || 0);
      setLogPage(data.page || 1);
      setLogPages(data.pages || 0);
    } catch { setLogs([]); }
    setLogLoading(false);
  }, [filterAction, filterUser, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  return (
    <div className="p-12 animate-in fade-in">
      <h3 className="text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">Journal d'activité</h3>
      <p className="text-slate-500 text-sm font-medium mb-8">Historique des connexions et actions de vos collaborateurs.</p>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-2xl border orion-settings-pref-card">
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Agent</label>
          <select
            title="Filtrer par agent"
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
          >
            <option value="">Tous les agents</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Action</label>
          <select
            title="Filtrer par action"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
          >
            <option value="">Toutes les actions</option>
            {Object.entries(actionLabels).map(([key, { label }]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Du</label>
          <input type="date" title="Date début" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Au</label>
          <input type="date" title="Date fin" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="flex items-end">
          <button type="button" onClick={() => { setFilterAction(''); setFilterUser(''); setDateFrom(''); setDateTo(''); }} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-200 px-3 py-2">
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Résumé */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-bold text-slate-400">{logTotal} entrée{logTotal > 1 ? 's' : ''}</span>
        {logLoading && <span className="text-xs text-slate-400 animate-pulse">Chargement...</span>}
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto rounded-2xl border orion-settings-table-shell">
        <table className="w-full text-left">
          <thead className="text-[10px] uppercase font-bold tracking-widest orion-settings-table-head text-slate-400">
            <tr>
              <th className="px-6 py-4">Date & Heure</th>
              <th className="px-6 py-4">Utilisateur</th>
              <th className="px-6 py-4">Action</th>
              <th className="px-6 py-4">Détails</th>
              <th className="px-6 py-4">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 orion-settings-table-body">
            {logs.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-xs text-slate-400 italic">Aucune activité trouvée.</td></tr>
            ) : logs.map((log: any) => {
              const actionInfo = actionLabels[log.action] || { label: log.action, color: 'bg-slate-100 text-slate-600' };
              return (
                <tr key={log.id} className="orion-settings-table-row text-sm">
                  <td className="px-6 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white orion-settings-avatar">
                        {(log.user_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-900">{log.user_name || 'Système'}</div>
                        <div className="text-[10px] text-slate-400">{log.user_role || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg ${actionInfo.color}`}>
                      {actionInfo.label}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500 max-w-[300px] truncate" title={log.details || ''}>
                    {log.details || '—'}
                  </td>
                  <td className="px-6 py-3 text-[10px] font-mono text-slate-400">
                    {log.ip_address || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {logPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button type="button" disabled={logPage <= 1} onClick={() => fetchLogs(logPage - 1)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">Précédent</button>
          <span className="text-xs font-bold text-slate-400">Page {logPage} / {logPages}</span>
          <button type="button" disabled={logPage >= logPages} onClick={() => fetchLogs(logPage + 1)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">Suivant</button>
        </div>
      )}
    </div>
  );
};

export default ActivityLogTab;
